import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { get } from 'lodash'
import {
	DatePicker,
	Input,
	InputNumber,
	Segmented,
	Select,
	message,
} from 'antd'
import dayjs from 'dayjs'
import { ArrowLeft, Plus, Save, Trash2, TriangleAlert } from 'lucide-react'
import DemoShell from './components/DemoShell'
import AsyncSelect, { OptionRow } from './components/AsyncSelect'
import { Card, Field } from './components/Field'
import InstallmentSchedule from './components/InstallmentSchedule'
import {
	createInvoice,
	getBusinessPartners,
	getItems,
	getSalesEmployees,
	getSerialNumbers,
} from './api'
import {
	buildSchedule,
	setAmountAt,
	shiftDatesFrom,
	spreadAmountFrom,
	toInstallmentsPayload,
} from './installments'
import { useInstallmentPlans, useWarehouses } from './hooks'
import {
	DATE_FORMAT,
	DEMO_INVOICES,
	INSTALLMENT_MONTH_OPTIONS,
	PLAN_TYPE_BASIC,
	PLAN_TYPE_VIP,
	TYPE_INVOICE_OPTIONS,
	demoInvoicePath,
} from './constants'
import {
	formatMoney,
	getApiErrorMessage,
	isUnauthorized,
	toNumber,
	udf,
} from './utils'

let lineId = 0
const emptyLine = () => ({
	key: `line-${++lineId}`,
	warehouseCode: '',
	itemCode: '',
	itemOption: null,
	serials: [],
	serialOptions: [],
	price: null,
	uImeiPurch: '',
	uCondition: '',
	uColor: '',
	uBox: '',
})

const CreateInvoice = () => {
	const navigate = useNavigate()
	const { getMe } = useSelector((state) => state.main)
	const { data: warehouses } = useWarehouses()
	const plans = useInstallmentPlans()

	const employeeId = get(getMe, 'EmployeeID', '')

	const [cardCode, setCardCode] = useState('')
	const [customerOption, setCustomerOption] = useState(null)
	const [salesPersonCode, setSalesPersonCode] = useState(null)
	const [salesPersonOption, setSalesPersonOption] = useState(null)
	const [docDate, setDocDate] = useState(dayjs())
	const [docDueDate, setDocDueDate] = useState(null)
	const [typeInvoice, setTypeInvoice] = useState('Naqd')
	const [firstPayment, setFirstPayment] = useState(null)
	const [planType, setPlanType] = useState(PLAN_TYPE_BASIC)
	const [months, setMonths] = useState(null)
	const [percent, setPercent] = useState(0)
	const [schedule, setSchedule] = useState([])
	const [scheduleError, setScheduleError] = useState('')
	const [icloud, setIcloud] = useState('')
	const [comments, setComments] = useState('')
	const [lines, setLines] = useState([emptyLine()])
	const [errors, setErrors] = useState({})
	const [serverError, setServerError] = useState('')
	const [submitting, setSubmitting] = useState(false)

	const isCredit = typeInvoice === 'Nasiya'

	// Eskisidagi «Месяц» / «Месяц B2B»: muddat ro'yxati tanlangan tarifdan
	// keladi, ustama foizi esa muddat bilan birga avtomatik qo'yiladi.
	const planRows = planType === PLAN_TYPE_VIP ? plans.vip : plans.basic
	const plansFallback = planRows.length === 0

	const monthOptions = useMemo(
		() =>
			plansFallback
				? INSTALLMENT_MONTH_OPTIONS.map((value) => ({
						value,
						label: `${value} oy`,
					}))
				: planRows.map((row) => ({
						value: row.months,
						label: `${row.months} oy${row.percent ? ` · ${row.percent}%` : ''}`,
					})),
		[planRows, plansFallback],
	)

	const selectMonths = (value) => {
		setMonths(value)
		const plan = planRows.find((row) => row.months === value)
		if (plan) setPercent(plan.percent)
	}

	const selectPlanType = (value) => {
		setPlanType(value)
		setMonths(null)
		setPercent(0)
	}

	const total = useMemo(
		() =>
			lines.reduce(
				(sum, line) =>
					sum + toNumber(line.price) * Math.max(line.serials.length, 1),
				0,
			),
		[lines],
	)

	const docDateKey = docDate ? docDate.format(DATE_FORMAT) : ''

	// Eskisidagi kabi: muddat / summa / boshlang'ich to'lov o'zgarsa grafik
	// qayta hisoblanadi. Qatorlarni qo'lda tahrirlash shundan keyin saqlanadi.
	useEffect(() => {
		if (!isCredit || !months) {
			setSchedule([])
			return
		}
		setSchedule(
			buildSchedule({
				months,
				startDate: docDate || dayjs(),
				total,
				firstPayment,
				percent,
			}),
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCredit, months, percent, total, firstPayment, docDateKey])

	const recalculate = () => {
		if (!months) {
			setScheduleError('Avval muddatni tanlang')
			return
		}
		setScheduleError('')
		setSchedule(
			buildSchedule({
				months,
				startDate: docDate || dayjs(),
				total,
				firstPayment,
				percent,
			}),
		)
	}

	const changeScheduleAmount = (index, amount) => {
		if (index === 0) {
			// 0-qator — boshlang'ich to'lov, qolgan oylar qayta bo'linadi
			setFirstPayment(amount)
			return
		}
		setSchedule((prev) =>
			index === 1
				? spreadAmountFrom(prev, 1, amount)
				: setAmountAt(prev, index, amount),
		)
	}

	const changeScheduleDate = (index, date) =>
		setSchedule((prev) => shiftDatesFrom(prev, index, date))

	const patchLine = (key, patch) =>
		setLines((prev) =>
			prev.map((line) => (line.key === key ? { ...line, ...patch } : line)),
		)

	const addLine = () => setLines((prev) => [...prev, emptyLine()])

	const removeLine = (key) =>
		setLines((prev) =>
			prev.length === 1 ? [emptyLine()] : prev.filter((l) => l.key !== key),
		)

	const validate = () => {
		const next = {}
		if (!cardCode) next.cardCode = 'Mijozni tanlang'
		if (!salesPersonCode && salesPersonCode !== 0)
			next.salesPersonCode = 'Sotuvchini tanlang'

		const filled = lines.filter(
			(line) => line.itemCode || line.serials.length || line.price,
		)
		if (filled.length === 0) next.lines = 'Kamida bitta tovar qo‘shing'

		const seenImei = new Set()
		lines.forEach((line, index) => {
			const lineErrors = {}
			if (!line.warehouseCode) lineErrors.warehouseCode = 'Omborni tanlang'
			if (!line.itemCode) lineErrors.itemCode = 'Tovarni tanlang'
			if (line.serials.length === 0) lineErrors.serials = 'IMEI ni tanlang'
			if (toNumber(line.price) <= 0) lineErrors.price = 'Narxni kiriting'

			line.serials.forEach((imei) => {
				if (seenImei.has(imei))
					lineErrors.serials = 'Bu IMEI allaqachon qo‘shilgan'
				seenImei.add(imei)
			})

			if (Object.keys(lineErrors).length) next[`line-${index}`] = lineErrors
		})

		if (isCredit && toNumber(firstPayment) > total)
			next.firstPayment = 'Boshlang‘ich to‘lov hujjat summasidan katta'

		if (isCredit && !months) {
			next.months = 'Nasiya muddatini tanlang'
			setScheduleError('Nasiya muddatini tanlang')
		} else if (isCredit) {
			setScheduleError('')
		}

		setErrors(next)
		return Object.keys(next).length === 0
	}

	const buildBody = () => ({
		cardCode,
		salesPersonCode: Number(salesPersonCode),
		docDate: docDate ? docDate.format(DATE_FORMAT) : undefined,
		docDueDate: docDueDate ? docDueDate.format(DATE_FORMAT) : undefined,
		comments: comments || undefined,
		U_TypeInvoice: typeInvoice,
		uTypeInvoice: typeInvoice,
		U_Icloud: icloud || undefined,
		uIcloud: icloud || undefined,
		U_IsAddOnWord: 'true',
		uIsAddOnWord: 'true',
		U_EmployeeId: String(employeeId ?? ''),
		uEmployeeId: String(employeeId ?? ''),
		...(isCredit && firstPayment !== null
			? {
					U_FirstPayment: toNumber(firstPayment),
					uFirstPayment: toNumber(firstPayment),
					firstPayment: toNumber(firstPayment),
				}
			: {}),
		// To'lov grafigi — eskisidagi `DocumentInstallments` bilan bir xil shakl,
		// backend qaysi nomni kutayotgani noma'lum, ikkalasi ham yuboriladi.
		...(isCredit && schedule.length
			? {
					documentInstallments: toInstallmentsPayload(schedule),
					DocumentInstallments: toInstallmentsPayload(schedule).map(
						({ total: rowTotal, dueDate }) => ({
							Total: rowTotal,
							DueDate: dueDate,
						}),
					),
				}
			: {}),
		documentLines: lines.map((line) => ({
			itemCode: line.itemCode,
			quantity: Math.max(line.serials.length, 1),
			price: toNumber(line.price),
			warehouseCode: line.warehouseCode,
			U_IMEI_PURCH: line.uImeiPurch || undefined,
			uimeipurch: line.uImeiPurch || undefined,
			U_Condition: line.uCondition || undefined,
			uCondition: line.uCondition || undefined,
			U_Color: line.uColor || undefined,
			uColor: line.uColor || undefined,
			U_Box: line.uBox || undefined,
			uBox: line.uBox || undefined,
			serialNumbers: line.serialOptions.map((option) => ({
				internalSerialNumber: option.distNumber,
				systemSerialNumber: option.sysNumber,
				quantity: 1,
			})),
		})),
	})

	const handleSubmit = () => {
		setServerError('')
		if (submitting) return
		if (!validate()) {
			message.error('Formada xatolar bor — qizil maydonlarni to‘g‘rilang')
			return
		}

		setSubmitting(true)
		createInvoice(buildBody())
			.then((created) => {
				message.success(`Hujjat №${created?.docNum ?? ''} yaratildi`)
				if (created?.docEntry) navigate(demoInvoicePath(created.docEntry))
				else navigate(DEMO_INVOICES)
			})
			.catch((err) => {
				if (isUnauthorized(err)) {
					navigate('/login', { replace: true })
					return
				}
				setServerError(getApiErrorMessage(err))
				setSubmitting(false)
			})
	}

	const lineError = (index) => errors[`line-${index}`] || {}

	return (
		<DemoShell
			title="Yangi hujjat"
			subtitle="A/R Invoice yaratish — barcha tovarlar seriya raqamlik (IMEI)"
			actions={
				<button
					type="button"
					onClick={() => navigate(DEMO_INVOICES)}
					className="inline-flex h-9 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:border-slate-300"
				>
					<ArrowLeft size={15} /> Hujjatlar
				</button>
			}
		>
			<div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_320px]">
				<div className="flex flex-col gap-4">
					{/* 1-blok */}
					<Card title="1. Mijoz va sotuvchi">
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
							<Field label="Mijoz" required>
								<AsyncSelect
									value={cardCode || undefined}
									status={errors.cardCode ? 'error' : ''}
									placeholder="Ism, kod yoki telefon"
									fetcher={(search) => getBusinessPartners({ search })}
									extraOptions={customerOption ? [customerOption] : []}
									mapOption={(bp) => ({
										value: bp.cardCode,
										title: bp.cardName,
										label: (
											<OptionRow
												title={bp.cardName}
												subtitle={[bp.phone, bp.cardCode]
													.filter(Boolean)
													.join(' · ')}
											/>
										),
									})}
									onChange={(value, option) => {
										setCardCode(value || '')
										setCustomerOption(value ? option : null)
									}}
								/>
								{errors.cardCode && (
									<span className="text-[11px] text-rose-500">
										{errors.cardCode}
									</span>
								)}
							</Field>

							<Field label="Sotuvchi" required>
								<AsyncSelect
									value={salesPersonCode ?? undefined}
									status={errors.salesPersonCode ? 'error' : ''}
									placeholder="Sotuvchini tanlang"
									fetcher={(search) => getSalesEmployees({ search })}
									extraOptions={salesPersonOption ? [salesPersonOption] : []}
									mapOption={(emp) => ({
										value: emp.slpCode,
										title: emp.slpName,
										label: (
											<OptionRow
												title={emp.slpName}
												subtitle={`slpCode: ${emp.slpCode}`}
											/>
										),
									})}
									onChange={(value, option) => {
										setSalesPersonCode(value ?? null)
										setSalesPersonOption(value != null ? option : null)
									}}
								/>
								{errors.salesPersonCode && (
									<span className="text-[11px] text-rose-500">
										{errors.salesPersonCode}
									</span>
								)}
							</Field>

							<Field label="Hujjat sanasi">
								<DatePicker
									className="w-full"
									format={DATE_FORMAT}
									value={docDate}
									onChange={setDocDate}
								/>
							</Field>

							<Field label="To‘lov muddati">
								<DatePicker
									className="w-full"
									format={DATE_FORMAT}
									value={docDueDate}
									onChange={setDocDueDate}
								/>
							</Field>
						</div>
					</Card>

					{/* 2-blok */}
					<Card title="2. Savdo turi">
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
							<Field label="Savdo turi" required>
								<Segmented
									block
									value={typeInvoice}
									onChange={setTypeInvoice}
									options={TYPE_INVOICE_OPTIONS.map((o) => ({
										value: o.value,
										label: o.label,
									}))}
								/>
							</Field>

							{isCredit && (
								<Field
									label="Boshlang‘ich to‘lov"
									hint="Grafikning 0-qatori — o‘zgarsa qolgan oylar qayta bo‘linadi"
								>
									<InputNumber
										className="w-full"
										min={0}
										status={errors.firstPayment ? 'error' : ''}
										value={firstPayment}
										onChange={setFirstPayment}
										placeholder="0"
									/>
									{errors.firstPayment && (
										<span className="text-[11px] text-rose-500">
											{errors.firstPayment}
										</span>
									)}
								</Field>
							)}

							<Field label="iCloud">
								<Input
									value={icloud}
									onChange={(e) => setIcloud(e.target.value)}
									placeholder="iCloud"
								/>
							</Field>
						</div>
					</Card>

					{/* 3-blok */}
					<Card
						title="3. Tovar qatorlari"
						subtitle="Tartib: ombor → tovar → IMEI → narx"
						right={
							<button
								type="button"
								onClick={addLine}
								className="inline-flex h-9 items-center gap-2 rounded-xl border border-slate-200 px-3 text-sm font-medium text-slate-600 transition hover:border-slate-300"
							>
								<Plus size={15} /> Qator qo‘shish
							</button>
						}
					>
						{errors.lines && (
							<p className="mb-3 text-[12px] text-rose-500">{errors.lines}</p>
						)}
						<div className="flex flex-col gap-3">
							{lines.map((line, index) => {
								const lineErr = lineError(index)
								return (
									<div
										key={line.key}
										className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4"
									>
										<div className="mb-3 flex items-center justify-between">
											<span className="text-[12px] font-semibold uppercase tracking-wide text-slate-400">
												Qator {index + 1}
											</span>
											<div className="flex items-center gap-3">
												<span className="text-[13px] text-slate-500">
													Summa:{' '}
													<span className="font-semibold text-slate-800">
														{formatMoney(
															toNumber(line.price) *
																Math.max(line.serials.length, 1),
															{ fallback: '0' },
														)}
													</span>
												</span>
												<button
													type="button"
													onClick={() => removeLine(line.key)}
													className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 transition hover:border-rose-200 hover:text-rose-500"
												>
													<Trash2 size={15} />
												</button>
											</div>
										</div>

										<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
											<Field label="Ombor" required>
												<Select
													className="w-full"
													showSearch
													optionFilterProp="label"
													status={lineErr.warehouseCode ? 'error' : ''}
													value={line.warehouseCode || undefined}
													placeholder="Ombor"
													onChange={(value) =>
														patchLine(line.key, {
															warehouseCode: value,
															// ombor o'zgarsa IMEI lar qayta tanlanadi
															serials: [],
															serialOptions: [],
														})
													}
													options={warehouses.map((whs) => ({
														value: whs.whsCode,
														label: `${whs.whsCode} · ${whs.whsName}`,
													}))}
												/>
												{lineErr.warehouseCode && (
													<span className="text-[11px] text-rose-500">
														{lineErr.warehouseCode}
													</span>
												)}
											</Field>

											<Field label="Tovar" required>
												<AsyncSelect
													value={line.itemCode || undefined}
													status={lineErr.itemCode ? 'error' : ''}
													placeholder="Nomi yoki IMEI"
													disabled={!line.warehouseCode}
													extraOptions={
														line.itemOption ? [line.itemOption] : []
													}
													fetcher={(search) =>
														getItems({
															search,
															whsCode: line.warehouseCode || undefined,
															availableOnly: 'Y',
														})
													}
													mapOption={(item) => ({
														value: item.itemCode,
														title: item.itemName || item.itemCode,
														raw: item,
														label: (
															<OptionRow
																title={item.itemName || item.itemCode}
																subtitle={[
																	item.itemCode,
																	udf(item, 'U_Condition'),
																	udf(item, 'U_Color'),
																]
																	.filter(Boolean)
																	.join(' · ')}
																right={`${toNumber(item.onHand)} dona`}
															/>
														),
													})}
													onChange={(value, option) => {
														const item = option?.raw || {}
														// TZ 5: qator UDF lari tanlangan tovardan avtomatik to'ldiriladi
														patchLine(line.key, {
															itemCode: value || '',
															itemOption: value ? option : null,
															serials: [],
															serialOptions: [],
															uCondition: udf(item, 'U_Condition') || '',
															uColor: udf(item, 'U_Color') || '',
															uBox: udf(item, 'U_Box') || '',
														})
													}}
												/>
												{lineErr.itemCode && (
													<span className="text-[11px] text-rose-500">
														{lineErr.itemCode}
													</span>
												)}
											</Field>

											<Field label="IMEI" required>
												<AsyncSelect
													mode="multiple"
													value={line.serials}
													status={lineErr.serials ? 'error' : ''}
													placeholder="IMEI tanlang"
													disabled={!line.itemCode}
													extraOptions={line.serialOptions}
													fetcher={(search) =>
														getSerialNumbers({
															itemCode: line.itemCode,
															whsCode: line.warehouseCode || undefined,
															startWith: search,
															pageSize: 50,
														})
													}
													mapOption={(serial) => ({
														value: serial.distNumber,
														title: serial.distNumber,
														distNumber: serial.distNumber,
														sysNumber: serial.sysNumber,
														// TZ 2.4: status !== 0 — ko'rinadi, lekin tanlab bo'lmaydi
														disabled: serial.status !== 0,
														label: (
															<OptionRow
																title={serial.distNumber}
																subtitle={`sysNumber: ${serial.sysNumber} · ${serial.whsCode}`}
																right={serial.status === 0 ? '' : 'band'}
															/>
														),
													})}
													onChange={(values, options) =>
														patchLine(line.key, {
															serials: values,
															serialOptions: options || [],
														})
													}
												/>
												{lineErr.serials && (
													<span className="text-[11px] text-rose-500">
														{lineErr.serials}
													</span>
												)}
											</Field>

											<Field label="Narx" required>
												<InputNumber
													className="w-full"
													min={0}
													status={lineErr.price ? 'error' : ''}
													value={line.price}
													onChange={(value) =>
														patchLine(line.key, { price: value })
													}
													placeholder="0"
												/>
												{lineErr.price && (
													<span className="text-[11px] text-rose-500">
														{lineErr.price}
													</span>
												)}
											</Field>
										</div>

										<div className="mt-3 grid grid-cols-1 gap-3 border-t border-slate-200 pt-3 sm:grid-cols-2 lg:grid-cols-5">
											<Field label="Miqdor" hint="IMEI soniga teng">
												<Input
													disabled
													value={Math.max(line.serials.length, 1)}
												/>
											</Field>
											<Field label="Sotib olingan IMEI">
												<Input
													value={line.uImeiPurch}
													onChange={(e) =>
														patchLine(line.key, { uImeiPurch: e.target.value })
													}
												/>
											</Field>
											<Field label="Holati">
												<Input
													value={line.uCondition}
													onChange={(e) =>
														patchLine(line.key, { uCondition: e.target.value })
													}
												/>
											</Field>
											<Field label="Rangi">
												<Input
													value={line.uColor}
													onChange={(e) =>
														patchLine(line.key, { uColor: e.target.value })
													}
												/>
											</Field>
											<Field label="Karobkasi">
												<Input
													value={line.uBox}
													onChange={(e) =>
														patchLine(line.key, { uBox: e.target.value })
													}
												/>
											</Field>
										</div>
									</div>
								)
							})}
						</div>
					</Card>

					{/* 4-blok — eskisidagi «Расчет» jadvali */}
					{isCredit && (
						<Card
							title="4. To‘lov grafigi"
							subtitle="Muddatni tanlang — oyma-oy grafik chiqadi, sana va summalarni tahrirlash mumkin"
						>
							<InstallmentSchedule
								planType={planType}
								monthOptions={monthOptions}
								plansFallback={plansFallback}
								months={months}
								percent={percent}
								rows={schedule}
								total={total}
								error={scheduleError}
								onPlanTypeChange={selectPlanType}
								onMonthsChange={selectMonths}
								onPercentChange={(value) => setPercent(value ?? 0)}
								onAmountChange={changeScheduleAmount}
								onDateChange={changeScheduleDate}
								onRecalculate={recalculate}
							/>
						</Card>
					)}
				</div>

				{/* 5-blok */}
				<div className="xl:sticky xl:top-24 xl:self-start">
					<Card title="5. Yakun">
						<div className="flex flex-col gap-4">
							<div className="rounded-xl bg-slate-50 p-3">
								<p className="text-[11px] uppercase tracking-wide text-slate-400">
									Qatorlar summasi
								</p>
								<p className="mt-1 text-2xl font-semibold text-slate-900">
									{formatMoney(total, { fallback: '0' })}
								</p>
							</div>

							<Field label="Izoh">
								<Input.TextArea
									rows={3}
									value={comments}
									onChange={(e) => setComments(e.target.value)}
									placeholder="Hujjat izohi"
								/>
							</Field>

							<div className="rounded-xl border border-dashed border-slate-200 p-3 text-[12px] text-slate-400">
								<p>
									U_IsAddOnWord:{' '}
									<span className="font-mono text-slate-600">true</span>
								</p>
								<p>
									U_EmployeeId:{' '}
									<span className="font-mono text-slate-600">
										{employeeId || '—'}
									</span>
								</p>
							</div>

							{!employeeId && (
								<div className="flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-[12px] text-amber-700">
									<TriangleAlert size={15} className="mt-0.5 shrink-0" />
									<span>
										Tizimga kirilmagan — U_EmployeeId bo‘sh ketadi. Avval login
										qiling.
									</span>
								</div>
							)}

							{serverError && (
								<div className="flex items-start gap-2 rounded-xl bg-rose-50 p-3 text-[12px] text-rose-700">
									<TriangleAlert size={15} className="mt-0.5 shrink-0" />
									<span>{serverError}</span>
								</div>
							)}

							<button
								type="button"
								onClick={handleSubmit}
								disabled={submitting}
								className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
							>
								<Save size={16} />
								{submitting ? 'Saqlanmoqda…' : 'Saqlash'}
							</button>
							<p className="text-center text-[11px] text-slate-400">
								Idempotentlik yo‘q — tugma faqat bir marta bosiladi
							</p>
						</div>
					</Card>
				</div>
			</div>
		</DemoShell>
	)
}

export default CreateInvoice
